<template>
	<div v-click-outside="hideCalendar" class="relative" :class="variantCls.root">
		<!-- loading indicator, rendered on server-side; we are showing fake input -->
		<div slot="placeholder" :class="variantCls.input" @click="toggleCalendar()">
			{{ value ? dayjs(value).format(format) : "" }}
		</div>
		<ClientOnly>
			<!-- this component will only be rendered on client-side -->
			<Transition
				:enter-class="variantCls.enter"
				:enter-active-class="variantCls.enterActive"
				:leave-active-class="variantCls.leaveActive"
				:leave-to-class="variantCls.leaveTo"
			>
				<JCalendar
					v-if="calendarVisible"
					:variant="componentInstanceVariants.calendar"
					:value="computedValue"
					:min-date="minDate"
					:max-date="maxDate"
					:years="years"
					:input-props="{ class: variantCls.input, placeholder }"
					:input-debounce="1000"
					:color="color"
					@input="onInput"
				/>
			</Transition>
		</ClientOnly>
	</div>
</template>

<script>
/**
 * @description This component can be used for picking one day
 * We are using https://vcalendar.io/
 * @param {ISOString | Date} value - it accepts String date or JavaScript Date object
 * @returns {ISOString} - it returns simplified ISOString like so "2019-01-24"
 */

import dayjs from "dayjs"

export default {
	props: {
		variant: {
			type: String,
			default: "default"
		},
		format: {
			type: String,
			default: "DD/MM/YYYY"
		},
		value: {
			// Accepts {String in ISO date format YYYY-MM-DD}, {Null} or {Date}
			validator(val) {
				return val === null || typeof val === "string" || val instanceof Date || typeof val === "object"
			},
			required: true,
			default: null
		},
		placeholder: {
			type: String,
			default: ""
		},
		// Minimum date that user can pick
		// E.g. disallow all past dates
		minDate: {
			validator(val) {
				return typeof val === typeof dayjs()
			}
		},
		// Maximum date that user can pick
		maxDate: {
			validator(val) {
				return typeof val === typeof dayjs()
			}
		},
		years: {
			type: Array,
			required: false
		},
		color: {
			type: String,
			default: "gray"
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			// NOTE:
			// prop `:select-attribute="selectAttribute"`
			// This can adjusts some bits of content but it doesn't solve hover state
			// thus theming sucks and we use gray color of the date picker as standard theming
			// otherwise we would have to have css overrides for calendar
			// selectAttribute: {
			//   content: {
			//     class: "bg-c2 hover:bg-c2 active:bg-c2 focus:bg-c2",
			//     contentClass: "text-c3"
			//   }
			// }
			calendarVisible: false,
			dayjs
		}
	},
	computed: {
		variantCls() {
			return this.$store.getters.variants("JDatePicker", this.variant).el
		},
		componentInstanceVariants() {
			return this.$store.getters.variants("JInputDateOfBirth", this.variant).componentInstanceVariants
		},
		computedValue() {
			if (!this.value) return null
			// If value is string, convert it to date
			if (typeof this.value === "string") return new Date(this.value)
			return this.value
		}
	},
	methods: {
		onInput(value) {
			if (this.disabled) return
			this.toggleCalendar()
			// const outputDate = dayjs(value).format("YYYY-MM-DD") // convert to simplified ISOString in utc
			// this.displayedDate = outputDate
			this.$emit("input", value)
		},
		toggleCalendar() {
			if (this.disabled) return
			this.calendarVisible = !this.calendarVisible
		},
		hideCalendar() {
			this.calendarVisible = false
		}
	}
}
</script>
